import { redirect, useRouteLoaderData } from 'react-router';
import { callServer } from './api/server';

/** The signed-in admin, as the server reports it on every page load. */
export interface AdminIdentity {
  id: string;
  email: string;
  role: string;
}

/** Route id of the signed-in area — its loader data is the admin. */
export const ADMIN_ROUTE_ID = 'admin';

const LOGIN_PATH = '/admin/login';

// "Not signed in" is a normal answer here, so it must not be reported as a
// lost session (see callServer's expectSignedIn).
const askWhoIsSignedIn = () =>
  callServer<AdminIdentity>('GET', '/api/v1/admin/session', { expectSignedIn: false });

/**
 * Loader for every signed-in screen: who is this? Anything other than a
 * clear yes — signed out, expired, admin removed, server unreachable —
 * goes to the login page. The server enforces access on every API call
 * regardless; this only decides which screen to show.
 */
export async function loadSession(): Promise<AdminIdentity> {
  const result = await askWhoIsSignedIn();
  if (!result.ok) throw redirect(LOGIN_PATH);
  return result.data;
}

/** Loader for the login page: no need to sign in twice. */
export async function redirectIfSignedIn(): Promise<null> {
  const result = await askWhoIsSignedIn();
  if (result.ok) throw redirect('/admin');
  return null;
}

/** The signed-in admin, inside any signed-in screen. */
export function useAdmin(): AdminIdentity {
  return useRouteLoaderData(ADMIN_ROUTE_ID) as AdminIdentity;
}
